import React from "react"
import PropTypes from "prop-types"
import { Button, Flex, Tooltip } from "antd"
import { InfoCircleOutlined } from "@ant-design/icons"
import { useTranslation } from "react-i18next"
import { Icon } from "ui/Icon"
import { formatListPrice } from "../../helpers"
import { getOriginalIfDiscounted, getRateComparison } from "../pricing"
import { GUEST_TYPES, getTotalGuests } from "../constants"
import PanelSection from "./PanelSection"
import styles from "./BookingPanel.module.css"

const RATE_BY_GUEST_TYPE = {
  adults: "pricePerPerson",
  children: "pricePerChild",
  infants: "pricePerInfant",
  passengers: "pricePerPassenger"
}

const UnitPrice = ({ rate, baseRate }) => {
  const { t } = useTranslation()
  if (!Number(rate)) return <span className={styles.guestPrice}>{t("booking_widget.experience.no_charge")}</span>
  const original = getOriginalIfDiscounted(baseRate, rate)
  return (
    <span className={styles.guestPrice}>
      {original != null && <span className={styles.struck}>{formatListPrice(original)}</span>}
      {formatListPrice(rate)}
    </span>
  )
}

UnitPrice.propTypes = {
  rate: PropTypes.number,
  baseRate: PropTypes.number
}

// Guest counters. Per-person trips show each type's price for the date (a multi-day trip's price for
// the whole trip); flat-rate trips show how many guests the rate includes and what each extra guest
// costs (extra guests of every type are billed at the per-person rate).
const GuestsSection = ({ experience, priceData, date, endDate, counts, minGuests, maxGuests, onChange, error }) => {
  const { t } = useTranslation()
  const isFlatRate = priceData?.pricing_type === "charge_flat_rate"
  const { rates, baseRates } = getRateComparison({ experience, priceData: priceData || {}, date, endDate })
  const totalGuests = getTotalGuests(counts)
  const isAtMax = maxGuests > 0 && totalGuests >= maxGuests
  const allowedTypes = GUEST_TYPES.filter(({ key }) => (key === "adults" ? priceData?.adults_allowed !== false : !!priceData?.[`${key}_allowed`]))

  const updateCount = (key, delta) => {
    const next = Math.max(0, (counts?.main?.[key] || 0) + delta)
    onChange({ ...counts, main: { ...counts?.main, [key]: next } })
  }

  let subtitle = null
  if (isFlatRate) {
    const includedGuests = priceData?.included_guests ?? priceData?.max_guests ?? 0
    const additionalGuests = Math.max(0, (maxGuests || 0) - includedGuests)
    const extraOriginal = getOriginalIfDiscounted(baseRates.pricePerPerson, rates.pricePerPerson)
    subtitle = (
      <Flex vertical className={styles.sectionSubtitle}>
        <span>{t("booking_widget.experience.flat_rate_included", { count: includedGuests })}</span>
        {additionalGuests > 0 && (
          <span>
            {t("booking_widget.experience.flat_rate_additional", { count: additionalGuests })} (+
            {extraOriginal != null && <span className={styles.struck}>{formatListPrice(extraOriginal)}</span>}
            {formatListPrice(rates.pricePerPerson)}
            {t("booking_widget.experience.per_person")})
          </span>
        )}
      </Flex>
    )
  } else if (maxGuests > 0) {
    subtitle = <span className={styles.sectionSubtitle}>{t("booking_widget.experience.guests_allowed", { min: minGuests || 1, max: maxGuests })}</span>
  }

  return (
    <PanelSection title={t("booking_widget.experience.add_guests")} subtitle={subtitle} error={error}>
      <Flex vertical gap={12}>
        {allowedTypes.map(({ key }) => {
          const label = t(`booking_widget.experience.guest_types.${key}`)
          const hintKey = `booking_widget.experience.guest_type_hints.${key}`
          const count = counts?.main?.[key] || 0
          return (
            <Flex key={key} align="center" justify="space-between" gap={12}>
              <Flex vertical>
                <Flex align="center" gap={4}>
                  <span className={styles.guestLabel}>{label}</span>
                  {key !== "passengers" && (
                    <Tooltip title={t(hintKey)}>
                      <InfoCircleOutlined className={styles.infoIcon} aria-label={t(hintKey)} />
                    </Tooltip>
                  )}
                </Flex>
                {!isFlatRate && <UnitPrice rate={rates[RATE_BY_GUEST_TYPE[key]]} baseRate={baseRates[RATE_BY_GUEST_TYPE[key]]} />}
              </Flex>
              <Flex align="center" gap={12}>
                <Button
                  shape="circle"
                  className={styles.counterButton}
                  aria-label={t("booking_widget.experience.decrease", { type: label })}
                  disabled={count === 0}
                  onClick={() => updateCount(key, -1)}
                  icon={<Icon type="feather" name="Minus" width={16} height={16} color="currentColor" />}
                />
                <span className={styles.counterValue} aria-live="polite">
                  {count}
                </span>
                <Button
                  shape="circle"
                  className={styles.counterButton}
                  aria-label={t("booking_widget.experience.increase", { type: label })}
                  disabled={isAtMax}
                  onClick={() => updateCount(key, 1)}
                  icon={<Icon type="feather" name="Plus" width={16} height={16} color="currentColor" />}
                />
              </Flex>
            </Flex>
          )
        })}
        {isAtMax && <span className={styles.hint}>{t("booking_widget.experience.max_guests_reached", { count: maxGuests })}</span>}
      </Flex>
    </PanelSection>
  )
}

GuestsSection.propTypes = {
  experience: PropTypes.object,
  priceData: PropTypes.object,
  date: PropTypes.string,
  // A dynamic multi-day trip's last day.
  endDate: PropTypes.string,
  counts: PropTypes.shape({ main: PropTypes.object, extra: PropTypes.object }).isRequired,
  minGuests: PropTypes.number,
  maxGuests: PropTypes.number,
  onChange: PropTypes.func.isRequired,
  error: PropTypes.string
}

GuestsSection.defaultProps = {
  experience: null,
  priceData: null,
  date: null,
  endDate: null,
  minGuests: 0,
  maxGuests: 0,
  error: null
}

export default GuestsSection
